import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import VehicleForm from 'components/account/vehicle/VehicleForm';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import useAfterVehicleRedirect from 'hooks/useAfterVehicleRedirect';
import useUpdateVehicleMutation from 'hooks/useUpdateVehicleMutation';
import { get, isEmpty, isEqual } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVehicleStore } from 'stores/vehicleStore';
import { Vehicle } from 'types/generatedSchemaTypes';
import { AccountStackParamList, RootNavProps } from 'types/navigation';
import Logger from 'utils/Logger';
import { getVehicleKeys } from 'utils/vehicleUtils';
import { useUiStore } from 'stores/uiStore';

const EditVehicleScreen: React.FC = () => {
  const { navigate, addListener } = useNavigation<RootNavProps>();
  const { params } =
    useRoute<RouteProp<AccountStackParamList, 'editVehicle'>>();
  const {
    isDiscarded,
    isDeleted,
    vehicleData: editVehicle,
    vehicleSaved,
    setVehicleSaved,
    resetVehicleStore,
  } = useVehicleStore((state) => state);
  const originalLicensePlate = params?.licensePlate;
  const openModalName = useUiStore((state) => state.sheetModal);
  const { navigateAfterVehicleSaved } = useAfterVehicleRedirect();
  const {
    customer: { vehicles },
  } = useContext(AccountContext);
  const [updatedLicensePlate, setUpdatedLicensePlate] = useState<string>();

  const originalSelectedVehicle = useMemo(() => {
    return vehicles?.find((currentVehicle: Vehicle) => {
      return (
        currentVehicle.licensePlate?.plateText ===
        (updatedLicensePlate ?? originalLicensePlate)
      );
    });
  }, [originalLicensePlate, vehicles, updatedLicensePlate]);

  // hasUnsavedChanges is thread as a special state combined with a ref
  // because it needs to be used inside the event listener
  const [hasUnsavedChanges, _setHasUnsavedChanges] = useState(false);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const setHasUnsavedChanges = (value: boolean) => {
    hasUnsavedChangesRef.current = value;
    _setHasUnsavedChanges(value);
  };

  const { updateVehicle, updating } = useUpdateVehicleMutation();

  const handleNavigation = (e: any) => {
    if (hasUnsavedChangesRef.current) {
      e?.preventDefault();
      useUiStore.getState().setSheetModal('unsavedEditVehicle');
    }
  };

  const handleRemoveConfirmation = () => {
    useUiStore.getState().setSheetModal('deleteVehicle');
  };

  const hasFormChanges = useCallback((): boolean => {
    if (isEmpty(editVehicle)) {
      return false;
    }
    const vehicleKeys = getVehicleKeys(originalSelectedVehicle);
    // Filter relevant paths
    const relevantPaths = vehicleKeys.filter((key) => {
      // Skip id and other metadata fields
      const skipFields = ['id', '__typename'];
      const isSkipField = skipFields.some((field) => key.includes(field));
      // Add evPlugType only for electric vehicles
      if (key.includes('evPlugType')) {
        return originalSelectedVehicle?.appSettings?.type === 'ELECTRIC';
      }
      return !isSkipField;
    });

    return relevantPaths.some((field) => {
      const originalValue = get(originalSelectedVehicle, field);
      const updatedValue = get(editVehicle, field);
      // If both values are null or undefined, consider them equal
      if (!originalValue && !updatedValue) {
        return false;
      }
      return !isEqual(originalValue, updatedValue);
    });
  }, [originalSelectedVehicle, editVehicle]);

  useEffect(() => {
    // Redirect to my vehicles screen if vehicle is not found
    if (!originalSelectedVehicle) {
      if (!isDeleted) {
        showToast('error.noVehicleSelected', 'error');
      }
      navigateAfterVehicleSaved();
    }
  }, [
    editVehicle,
    originalSelectedVehicle,
    openModalName,
    originalLicensePlate,
    navigateAfterVehicleSaved,
    isDeleted,
  ]);

  // Update form status when a field changes.
  // Used to enable/disable update button.
  useEffect(() => {
    setHasUnsavedChanges(hasFormChanges());
  }, [hasFormChanges]);

  // Redirect to my vehicles screen if the modal is discarded
  useEffect(() => {
    if (isDiscarded) {
      setHasUnsavedChanges(false);
      navigateAfterVehicleSaved();
    }
  }, [isDiscarded, navigateAfterVehicleSaved]);

  // Prevent navigation if there are unsaved changes
  useEffect(() => {
    addListener('beforeRemove', handleNavigation);
    return () => {
      addListener('beforeRemove', handleNavigation);
    };
  }, [addListener, navigate]);

  const handleUpdateVehicle = useCallback(async () => {
    if (!editVehicle?.id) {
      Logger.error('Update vehicle, editVehicle has no id');
      showToast('error.updateVehicle', 'error');
      return;
    }
    await updateVehicle({
      variables: {
        input: {
          licensePlate: {
            plateText: editVehicle.licensePlate?.plateText ?? '',
            countryCode: editVehicle.licensePlate?.countryCode ?? undefined,
          },
          id: editVehicle.id,
          name: editVehicle.name,
          cameraRecognition: {
            allowed: editVehicle.cameraRecognition?.allowed ?? false,
            enabled: editVehicle.cameraRecognition?.enabled ?? false,
            countryCodes: editVehicle.cameraRecognition?.countryCodes,
          },
          appSettings: {
            icon: editVehicle.appSettings?.icon,
            color: editVehicle.appSettings?.color,
            type: editVehicle.appSettings?.type,
            evPlugType: editVehicle.appSettings?.evPlugType,
          },
        },
      },
      onCompleted: (data) => {
        setUpdatedLicensePlate(data.updateVehicle.licensePlate.plateText);
        showToast('success.updateVehicle', 'success');
        setHasUnsavedChanges(false);
        resetVehicleStore();
        navigateAfterVehicleSaved();
      },
    });
  }, [
    editVehicle,
    updateVehicle,
    navigateAfterVehicleSaved,
    resetVehicleStore,
  ]);

  useEffect(() => {
    if (vehicleSaved) {
      handleUpdateVehicle();
      setVehicleSaved(false);
      useUiStore.getState().setSheetModal('');
    }
  }, [vehicleSaved, handleUpdateVehicle, setVehicleSaved]);

  return (
    <VehicleForm
      isNew={false}
      onSubmit={handleUpdateVehicle}
      onDelete={handleRemoveConfirmation}
      loading={updating}
      hasUnsavedChanges={hasUnsavedChanges}
    />
  );
};

export default EditVehicleScreen;
